import React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import LockOverlay from './LockOverlay';

/**
 * Client View Editor — the "website builder" surface for the client-
 * facing appearance of a board. Sits in place of Board Settings when
 * open, so the creator can shape the exact experience their client
 * will see (cover, welcome, header) while still standing on the board
 * they're editing.
 *
 * All fields persist through `handleUpdateSettings` (identical to the
 * pattern used by Board Settings). Empty values fall back through the
 * template chain, so partial customisation is always safe.
 *
 * A live preview iframe of the real Client View is managed by the
 * parent (`BoardEditor`) and toggled through `onPreviewToggle`. This
 * component owns no state — everything reads from `board`.
 */
const ClientViewEditorSidebar = ({
  t,
  board,
  featureAccess,
  handleUpdateSettings,
  onClose,
  onPreviewToggle,
  previewAsClient,
  hidePreviewToggle = false,
}) => {
  const isInspiration = board?.board_type === 'inspiration';

  return (
    <div
      className="editor-sidebar client-view-editor-sidebar"
      data-testid="client-view-editor-sidebar"
      style={{ width: '380px' }}
    >
      {/* ── Header ────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 20,
      }}>
        <div>
          <p style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '10px',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'var(--kahla-taupe)',
            margin: 0,
            fontWeight: 500,
          }}>
            {t.client_view_editor_eyebrow || 'CLIENT VIEW EDITOR'}
          </p>
          <h3 style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '20px',
            margin: '6px 0 4px',
            lineHeight: 1.2,
          }}>
            {t.client_view_editor_title || 'What your client sees'}
          </h3>
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: '13px',
            lineHeight: 1.5,
            color: 'var(--kahla-text-light)',
            margin: 0,
          }}>
            {t.client_view_editor_subtitle || 'Shape the exact experience — changes save instantly.'}
          </p>
        </div>
        <button
          type="button"
          data-testid="close-client-view-editor-btn"
          onClick={onClose}
          aria-label={t.close || 'Close'}
          title={t.close || 'Close'}
          style={{
            width: 32,
            height: 32,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: '1px solid var(--kahla-border)',
            color: 'var(--kahla-charcoal)',
            fontSize: 16,
            lineHeight: 1,
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'background 160ms ease, border-color 160ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--kahla-cream-light)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          ✕
        </button>
      </div>

      {/* ── Preview as Client toggle ─────────────────────────────── */}
      {!hidePreviewToggle && (
      <button
        type="button"
        data-testid="preview-as-client-toggle"
        onClick={onPreviewToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          width: '100%',
          padding: '12px 16px',
          marginBottom: '24px',
          background: previewAsClient ? 'var(--kahla-charcoal, #2a2620)' : 'var(--kahla-cream, #f6f1e7)',
          color: previewAsClient ? '#f6f1e7' : 'var(--kahla-charcoal)',
          border: '1px solid ' + (previewAsClient ? 'var(--kahla-charcoal, #2a2620)' : '#d8c597'),
          cursor: 'pointer',
          fontFamily: 'Playfair Display, serif',
          fontSize: '13px',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          fontWeight: 500,
          transition: 'background 200ms ease, color 200ms ease, transform 160ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        <span aria-hidden="true" style={{ fontSize: '14px' }}>◐</span>
        <span>
          {previewAsClient
            ? (t.exit_client_preview || 'Exit preview')
            : (t.preview_as_client_inline || 'Preview as client')}
        </span>
      </button>
      )}

      {/* ── Board Cover ──────────────────────────────────────────── */}
      <div className="settings-panel" style={{ position: 'relative' }} data-testid="cve-board-cover-panel">
        <p className="settings-panel-title">{t.board_cover_panel_title}</p>
        <p style={{
          fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '14px',
          lineHeight: 1.6, fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic',
        }}>
          {t.board_cover_panel_desc}
        </p>
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          cursor: featureAccess.allow_board_cover ? 'pointer' : 'not-allowed',
          fontSize: '13px', padding: '4px 0',
          opacity: featureAccess.allow_board_cover ? 1 : 0.55,
        }}>
          <input
            type="checkbox"
            checked={featureAccess.allow_board_cover && board.board_cover_enabled === true}
            disabled={!featureAccess.allow_board_cover}
            onChange={(e) => handleUpdateSettings({ board_cover_enabled: e.target.checked })}
            style={{ width: '16px', height: '16px', accentColor: '#C9A96E', marginTop: '2px' }}
            data-testid="board-cover-toggle"
          />
          <span style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ color: 'var(--kahla-text)' }}>{t.board_cover_enable_label}</span>
            <span style={{
              fontSize: '11px', color: 'var(--kahla-text-light)',
              fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', lineHeight: 1.5,
            }}>
              {t.board_cover_enable_hint}
            </span>
          </span>
        </label>

        {featureAccess.allow_board_cover && board.board_cover_enabled === true && (
          <div style={{ marginTop: '14px', paddingLeft: '26px', display: 'flex', flexDirection: 'column', gap: '14px' }} data-testid="board-cover-fields">
            <p style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic', fontSize: '12px', lineHeight: 1.55,
              color: 'var(--kahla-text-light)', margin: 0,
            }} data-testid="board-cover-inline-hint">
              {t.inline_texts_on_page_hint || 'Titles and texts are edited directly on the page.'}
            </p>
            <div>
              <label style={{ display: 'block', fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '8px' }}>
                {t.board_cover_image_source_label}
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="board_cover_image_source"
                    checked={(board.board_cover_image_source || 'first_image') === 'first_image'}
                    onChange={() => handleUpdateSettings({ board_cover_image_source: 'first_image' })}
                    style={{ accentColor: '#C9A96E' }}
                    data-testid="board-cover-source-first"
                  />
                  <span>{t.board_cover_source_first}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="board_cover_image_source"
                    checked={board.board_cover_image_source === 'custom'}
                    onChange={() => handleUpdateSettings({ board_cover_image_source: 'custom' })}
                    style={{ accentColor: '#C9A96E' }}
                    data-testid="board-cover-source-custom"
                  />
                  <span>{t.board_cover_source_custom}</span>
                </label>
              </div>
              {board.board_cover_image_source === 'custom' && (
                <div style={{ marginTop: '12px', paddingLeft: '24px' }}>
                  {board.board_cover_custom_image_url && (
                    <img
                      src={(board.board_cover_custom_image_url || '').startsWith('r2/')
                        ? `${process.env.REACT_APP_BACKEND_URL}/api/images/${board.board_cover_custom_image_url}`
                        : board.board_cover_custom_image_url}
                      alt="Cover preview"
                      data-testid="board-cover-preview-img"
                      style={{
                        display: 'block', width: '100%', maxWidth: '320px',
                        height: '120px', objectFit: 'cover',
                        border: '1px solid var(--kahla-border)', marginBottom: '10px',
                      }}
                    />
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    data-testid="board-cover-upload-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const formData = new FormData();
                      formData.append('file', f);
                      try {
                        const Cookies = (await import('js-cookie')).default;
                        const tok = Cookies.get('kahla_token') || '';
                        const res = await fetch(
                          `${process.env.REACT_APP_BACKEND_URL}/api/boards/${board.id}/cover-image`,
                          { method: 'POST', headers: { Authorization: `Bearer ${tok}` }, body: formData },
                        );
                        if (!res.ok) throw new Error(await res.text());
                        const out = await res.json();
                        handleUpdateSettings({
                          board_cover_custom_image_url: out.board_cover_custom_image_url,
                          board_cover_image_source: 'custom',
                        });
                      } catch (err) {
                        console.error('Cover upload failed', err);
                      } finally {
                        e.target.value = '';
                      }
                    }}
                    style={{ fontSize: '12px' }}
                  />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={board.board_cover_show_skip === true}
                  onChange={(e) => handleUpdateSettings({ board_cover_show_skip: e.target.checked })}
                  style={{ accentColor: '#C9A96E' }}
                  data-testid="board-cover-show-skip"
                />
                <span>{t.board_cover_show_skip_label}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={board.board_cover_first_visit_only === true}
                  onChange={(e) => handleUpdateSettings({ board_cover_first_visit_only: e.target.checked })}
                  style={{ accentColor: '#C9A96E' }}
                  data-testid="board-cover-first-visit-only"
                />
                <span>{t.board_cover_first_visit_only_label}</span>
              </label>
            </div>
          </div>
        )}

        {!featureAccess.allow_board_cover && <LockOverlay />}
      </div>

      {/* ── Concept (Inspiration Boards only) — the "Client view header"
          option for Client Boards was removed: Welcome is the single
          introduction the client reads. ── */}
      {isInspiration && (
      <div className="settings-panel" data-testid="cve-client-header-panel">
        <>
            <p className="settings-panel-title">{t.concept_panel_title || 'Concept'}</p>
            <p style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: '13px',
              lineHeight: 1.55,
              color: 'var(--kahla-text-light)',
              margin: '-2px 0 14px',
            }}>
              {t.concept_panel_desc || 'A short description shown at the top of the board — the mood, story, or direction this concept explores.'}
            </p>
            <label style={{
              display: 'block',
              fontSize: '11px',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              marginBottom: '6px',
            }}>
              {t.concept_description_label || 'Concept description'}
            </label>
            <textarea
              defaultValue={board.board_subtitle || ''}
              maxLength={280}
              rows={3}
              onBlur={(e) => {
                const next = e.target.value.slice(0, 280);
                if (next !== (board.board_subtitle || '')) {
                  handleUpdateSettings({ board_subtitle: next });
                }
              }}
              placeholder={t.concept_description_placeholder || 'e.g. A study of soft afternoon light and natural materials for editorial portraits.'}
              data-testid="concept-description-input"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                fontSize: '13px',
                fontFamily: 'inherit',
                background: 'var(--kahla-white)',
                border: '1px solid var(--kahla-border)',
                color: 'var(--kahla-text)',
                outline: 'none',
                resize: 'vertical',
                lineHeight: 1.5,
              }}
            />
            <span style={{
              fontSize: '11px',
              color: 'var(--kahla-text-light)',
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              lineHeight: 1.5,
              display: 'block',
              marginTop: '6px',
            }}>
              {t.concept_description_hint || 'Displayed at the top of the board, under the title.'}
            </span>
          </>
      </div>
      )}

      {/* ── Welcome (Client Board only) ─────────────────────────── */}
      {!isInspiration && (
      <div className="settings-panel" style={{ position: 'relative' }} data-testid="cve-custom-welcome-panel">
        <p className="settings-panel-title">{t.custom_welcome_title}</p>
        <p
          style={{
            fontSize: '12px',
            color: 'var(--kahla-text-light)',
            marginBottom: '14px',
            lineHeight: 1.6,
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: 'italic',
          }}
        >
          {t.custom_welcome_desc}
        </p>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            cursor: featureAccess.allow_custom_welcome ? 'pointer' : 'not-allowed',
            fontSize: '13px',
            padding: '4px 0',
            opacity: featureAccess.allow_custom_welcome ? 1 : 0.55,
          }}
        >
          <input
            type="checkbox"
            checked={featureAccess.allow_custom_welcome && board.use_custom_welcome === true}
            disabled={!featureAccess.allow_custom_welcome}
            onChange={(e) => handleUpdateSettings({ use_custom_welcome: e.target.checked })}
            style={{ width: '16px', height: '16px', accentColor: '#C9A96E', marginTop: '2px' }}
            data-testid="use-custom-welcome-toggle"
          />
          <span style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ color: 'var(--kahla-text)' }}>{t.use_custom_welcome_label}</span>
            <span style={{
              fontSize: '11px',
              color: 'var(--kahla-text-light)',
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              lineHeight: 1.5,
            }}>
              {t.use_custom_welcome_hint}
            </span>
          </span>
        </label>

        {featureAccess.allow_custom_welcome && board.use_custom_welcome === true && (
          <div style={{ marginTop: '14px', paddingLeft: '26px' }} data-testid="custom-welcome-fields">
            <p style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic', fontSize: '12px', lineHeight: 1.55,
              color: 'var(--kahla-text-light)', margin: 0,
            }} data-testid="custom-welcome-inline-hint">
              {t.inline_texts_on_page_hint || 'Titles and texts are edited directly on the page.'}
            </p>
          </div>
        )}

        {!featureAccess.allow_custom_welcome && <LockOverlay />}
      </div>
      )}

      {/* Footer hint — where sections editing will land in the next phase */}
      {!isInspiration && (
        <>
          <div style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '10px',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'var(--kahla-taupe)',
            margin: '18px 0 10px',
            fontWeight: 500,
          }}>
            {t.cve_sections_eyebrow || 'SECTIONS'}
          </div>
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: '12px',
            lineHeight: 1.6,
            color: 'var(--kahla-text-light)',
            margin: '0 0 18px',
          }}>
            {t.cve_sections_help || 'Rename or hide the sections your client sees. Empty fields inherit the workspace default.'}
          </p>

          {/* ── Use Workspace Defaults toggle ─────────────────────────
              Plan-gated master switch (`allow_client_view_override`):
              • Basic → toggle disabled at True; hint invites upgrade.
              • Pro / Signature → toggle freely; when OFF, every
                per-section title/description input below activates
                and the ClientView starts resolving from board fields
                first. */}
          <WorkspaceDefaultsToggle
            t={t}
            board={board}
            featureAccess={featureAccess}
            handleUpdateSettings={handleUpdateSettings}
          />

          {(() => {
            // Editability of the section inputs is derived from:
            //   plan cap `allow_client_view_override` AND board flag
            //   `use_workspace_defaults === false`.
            // When either fails, inputs render disabled with a soft
            // "inherits workspace default" hint underneath so the
            // creator understands why they can't type.
            const canOverride = featureAccess.allow_client_view_override === true
              && board.use_workspace_defaults === false;

            // Section registry — every reorderable section declares its
            // props here once. The DnD list renders them in whatever
            // order `board.section_order` (or the canonical default)
            // dictates. Final Decision stays outside because it's a
            // banner, not a section in the render loop.
            const CANONICAL = ['gallery', 'client_uploads', 'notes', 'documents'];
            const orderFromBoard = Array.isArray(board.section_order) && board.section_order.length
              ? board.section_order
                  .map(s => (typeof s === 'string' ? s : s?.key))
                  .filter(k => CANONICAL.includes(k))
              : [];
            const order = [
              ...orderFromBoard,
              ...CANONICAL.filter(k => !orderFromBoard.includes(k)),
            ];

            const sectionRegistry = {
              gallery: {
                testId: 'cve-section-gallery',
                defaultLabel: t.card_gallery_section || 'Gallery',
                visibilityField: 'hide_gallery',
                visibilityAllowed: true,
                invertVisibility: true,
              },
              client_uploads: {
                testId: 'cve-section-inspirations',
                defaultLabel: t.card_inspirations_section || 'Client Inspirations',
                visibilityField: 'allow_client_uploads',
                visibilityAllowed: featureAccess.allow_client_uploads !== false,
              },
              notes: {
                testId: 'cve-section-notes',
                defaultLabel: t.card_notes_section || 'Conversation',
                visibilityField: 'allow_notes',
                visibilityAllowed: featureAccess.allow_notes !== false,
              },
              documents: {
                testId: 'cve-section-documents',
                defaultLabel: t.card_documents_section || 'Documents',
                visibilityField: 'allow_documents',
                visibilityAllowed: featureAccess.allow_documents !== false,
              },
            };

            return (
              <>
                <ReorderableSections
                  t={t}
                  board={board}
                  order={order}
                  registry={sectionRegistry}
                  canReorder={canOverride}
                  handleUpdateSettings={handleUpdateSettings}
                  canOverride={canOverride}
                />

                <FinalDecisionSectionEditor
                  t={t}
                  board={board}
                />
              </>
            );
          })()}
        </>
      )}

      <p style={{
        fontFamily: 'Cormorant Garamond, Playfair Display, serif',
        fontStyle: 'italic',
        fontSize: '12px',
        lineHeight: 1.55,
        color: 'var(--kahla-text-light)',
        margin: '18px 0 24px',
        padding: '12px 14px',
        background: 'rgba(166,152,136,0.06)',
        border: '1px solid rgba(166,152,136,0.18)',
      }}>
        {t.client_view_editor_sections_soon || 'Section reordering and custom section types are coming next. All client-facing settings live here.'}
      </p>
    </div>
  );
};

/**
 * Compact editor for a single client-facing section — visibility
 * checkbox + title input + description textarea. All fields save on
 * blur via `handleUpdateSettings`, so the live-preview iframe reloads
 * with the freshly-saved copy. Empty title/description intentionally
 * fall through to the workspace default on the ClientView side.
 */
const SectionEditor = ({
  testId,
  t,
  board,
  handleUpdateSettings,
  defaultLabel,
  visibilityField,
  visibilityAllowed,
  invertVisibility = false,
  dragHandleProps = null,
  isDragging = false,
}) => {
  // `invertVisibility` covers fields like `hide_gallery` where a truthy
  // value MEANS hidden. The checkbox always reads "section visible", so
  // for inverted fields we render !value and write !checked.
  const rawValue = board[visibilityField];
  const isVisible = invertVisibility ? rawValue !== true : rawValue !== false;
  return (
    <div className="settings-panel" data-testid={testId} style={{
      position: 'relative',
      background: isDragging ? 'var(--kahla-white)' : undefined,
      boxShadow: isDragging ? '0 6px 24px rgba(42,38,32,0.12)' : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        {dragHandleProps && (
          <button
            type="button"
            aria-label={t.cve_drag_section || 'Reorder section'}
            title={t.cve_drag_section || 'Reorder section'}
            data-testid={`${testId}-drag-handle`}
            {...dragHandleProps}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '22px',
              height: '22px',
              marginTop: '1px',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: dragHandleProps.style?.cursor || 'grab',
              color: 'var(--kahla-taupe, #a69888)',
              flexShrink: 0,
              touchAction: 'none',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <circle cx="4" cy="3"  r="1.2" fill="currentColor" />
              <circle cx="10" cy="3" r="1.2" fill="currentColor" />
              <circle cx="4" cy="7"  r="1.2" fill="currentColor" />
              <circle cx="10" cy="7" r="1.2" fill="currentColor" />
              <circle cx="4" cy="11" r="1.2" fill="currentColor" />
              <circle cx="10" cy="11" r="1.2" fill="currentColor" />
            </svg>
          </button>
        )}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          cursor: visibilityAllowed ? 'pointer' : 'not-allowed',
          fontSize: '13px', padding: '4px 0',
          opacity: visibilityAllowed ? 1 : 0.6,
          flex: 1,
        }}>
        <input
          type="checkbox"
          checked={visibilityAllowed && isVisible}
          disabled={!visibilityAllowed}
          onChange={(e) => {
            const next = invertVisibility ? !e.target.checked : e.target.checked;
            handleUpdateSettings({ [visibilityField]: next });
          }}
          style={{ width: '16px', height: '16px', accentColor: '#C9A96E', marginTop: '2px' }}
          data-testid={`${testId}-visibility`}
        />
        <span style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '14px',
          letterSpacing: '0.02em',
          color: 'var(--kahla-text)',
        }}>
          {defaultLabel}
        </span>
      </label>
      </div>
    </div>
  );
};

/**
 * Final Decision has a special enable/disable path (secure email
 * invitation), so we don't expose a naive visibility checkbox here.
 * Instead: if FD is active on the board, the creator can rename the
 * banner heading and body. If it's off, we show a quiet hint pointing
 * back to the invitation flow.
 */
const FinalDecisionSectionEditor = ({ t, board }) => {
  const active = board.final_decision_mode === true;
  return (
    <div className="settings-panel" data-testid="cve-section-final-decision" style={{ position: 'relative' }}>
      <p className="settings-panel-title" style={{ marginBottom: 4 }}>
        {t.card_final_decision_section || 'Final Decision'}
      </p>
      <p style={{
        fontFamily: 'Cormorant Garamond, Playfair Display, serif',
        fontStyle: 'italic',
        fontSize: '12px',
        lineHeight: 1.55,
        color: 'var(--kahla-text-light)',
        margin: '0 0 4px',
      }}>
        {active
          ? (t.cve_fd_active_hint || 'The banner heading and text are edited directly on the page.')
          : (t.cve_fd_inactive_hint || 'Final Decision is enabled through the secure invitation flow (Board Settings → Final Decision).')}
      </p>
    </div>
  );
};

/**
 * The master "Use Workspace Defaults" switch. Rendered above the four
 * section editors. On Basic plans it's always ON and locked with an
 * upgrade hint. On Pro/Signature the creator toggles freely; flipping
 * OFF activates every per-section title/description input below.
 *
 * We write `use_workspace_defaults` — the ClientView reads the same
 * flag and picks board overrides only when it's False.
 */
const WorkspaceDefaultsToggle = ({ t, board, featureAccess, handleUpdateSettings }) => {
  const allowOverride = featureAccess.allow_client_view_override === true;
  const isOn = board.use_workspace_defaults !== false; // default True
  return (
    <div
      className="settings-panel"
      data-testid="cve-workspace-defaults-panel"
      style={{ position: 'relative' }}
    >
      <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        cursor: allowOverride ? 'pointer' : 'not-allowed',
        fontSize: '13px',
        padding: '4px 0',
        opacity: allowOverride ? 1 : 0.7,
      }}>
        <input
          type="checkbox"
          checked={isOn}
          disabled={!allowOverride}
          onChange={(e) => handleUpdateSettings({ use_workspace_defaults: e.target.checked })}
          style={{ width: '16px', height: '16px', accentColor: '#C9A96E', marginTop: '2px' }}
          data-testid="cve-workspace-defaults-toggle"
        />
        <span style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <span style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '14px',
            color: 'var(--kahla-text)',
          }}>
            {t.cve_use_workspace_defaults || 'Use Workspace Defaults'}
          </span>
          <span style={{
            fontSize: '11px',
            color: 'var(--kahla-text-light)',
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: 'italic',
            lineHeight: 1.55,
          }}>
            {allowOverride
              ? (isOn
                  ? (t.cve_workspace_defaults_on_hint || 'Section names come from Branding → Client View Texts. Turn off to customize this board only.')
                  : (t.cve_workspace_defaults_off_hint || 'This board uses its own section names and descriptions.'))
              : (t.cve_workspace_defaults_locked_hint || 'Available on Pro and Signature. All boards inherit the workspace defaults on your current plan.')}
          </span>
        </span>
      </label>
    </div>
  );
};

/**
 * Sortable list wrapper for the four reorderable client-facing
 * sections. Renders each SectionEditor row with a drag handle (when
 * `canReorder` is true) and persists the new order to the board via
 * `handleUpdateSettings({section_order: [...]})` immediately after
 * the drop. The router silently drops the write on Basic plans, so
 * the UX degrades gracefully if a downgrade happens between paint
 * and drag.
 */
const ReorderableSections = ({
  t,
  board,
  order,
  registry,
  canReorder,
  canOverride,
  handleUpdateSettings,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── One-time reorder hint ────────────────────────────────────────
  // Surfaces a soft "drag ⋮⋮ to reorder" pill above the section list
  // the FIRST time a creator opens the CV Editor with reordering
  // actually enabled. Fades in for a beat, then fades out on its
  // own (3s), or dismisses permanently on any drag. Stored in
  // localStorage so it never returns for the same browser.
  const HINT_KEY = 'maestra.cve.reorder_hint_seen';
  const [hintVisible, setHintVisible] = React.useState(false);
  React.useEffect(() => {
    if (!canReorder) return undefined;
    try {
      if (window.localStorage.getItem(HINT_KEY)) return undefined;
    } catch (_e) { /* private mode / disabled — ignore */ }
    setHintVisible(true);
    const dismissTimer = setTimeout(() => setHintVisible(false), 5200);
    return () => clearTimeout(dismissTimer);
  }, [canReorder]);
  const dismissHint = React.useCallback(() => {
    setHintVisible(false);
    try { window.localStorage.setItem(HINT_KEY, '1'); } catch (_e) { /* ignore */ }
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    dismissHint();
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const nextKeys = arrayMove(order, oldIndex, newIndex);
    // Persist the full ordering as {key, visible} objects. Visibility
    // stays derived from the board's `allow_*` / `hide_gallery` flags
    // (unchanged by drag) — sending `visible:true` here just keeps the
    // schema valid; the ClientView still respects the flags.
    const payload = nextKeys.map(k => ({ key: k, visible: true }));
    handleUpdateSettings({ section_order: payload });
  };

  const inner = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
      {canReorder && hintVisible && (
        <button
          type="button"
          onClick={dismissHint}
          data-testid="cve-reorder-hint"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            padding: '10px 14px',
            marginBottom: '12px',
            background: 'var(--kahla-cream, #f6f1e7)',
            border: '1px solid #d8c597',
            cursor: 'pointer',
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: '13px',
            color: 'var(--kahla-charcoal)',
            textAlign: 'left',
            animation: 'cve-hint-in 260ms ease-out',
            transition: 'opacity 400ms ease',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" style={{ flexShrink: 0, color: 'var(--kahla-taupe, #a69888)' }}>
            <circle cx="4" cy="3" r="1.2" fill="currentColor" />
            <circle cx="10" cy="3" r="1.2" fill="currentColor" />
            <circle cx="4" cy="7" r="1.2" fill="currentColor" />
            <circle cx="10" cy="7" r="1.2" fill="currentColor" />
            <circle cx="4" cy="11" r="1.2" fill="currentColor" />
            <circle cx="10" cy="11" r="1.2" fill="currentColor" />
          </svg>
          <span style={{ flex: 1, lineHeight: 1.5 }}>
            {t.cve_reorder_hint || 'Drag by ⋮⋮ to reorder sections.'}
          </span>
          <span aria-hidden="true" style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '13px',
            color: 'var(--kahla-text-light)',
            flexShrink: 0,
          }}>×</span>
        </button>
      )}
      {order.map((key) => {
        const cfg = registry[key];
        if (!cfg) return null;
        return (
          <SortableSectionRow
            key={key}
            id={key}
            t={t}
            board={board}
            handleUpdateSettings={handleUpdateSettings}
            canReorder={canReorder}
            canOverride={canOverride}
            cfg={cfg}
          />
        );
      })}
    </div>
  );

  if (!canReorder) {
    // Plan doesn't allow per-board overrides — render the same rows
    // without a DndContext so nothing is even attempting to drag.
    return inner;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        {inner}
      </SortableContext>
    </DndContext>
  );
};

const SortableSectionRow = ({ id, t, board, handleUpdateSettings, canReorder, canOverride, cfg }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !canReorder,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 5 : 'auto',
  };
  const dragHandleProps = canReorder
    ? {
        ...attributes,
        ...listeners,
        style: { cursor: isDragging ? 'grabbing' : 'grab' },
      }
    : null;
  return (
    <div ref={setNodeRef} style={style}>
      <SectionEditor
        testId={cfg.testId}
        t={t}
        board={board}
        handleUpdateSettings={handleUpdateSettings}
        defaultLabel={cfg.defaultLabel}
        visibilityField={cfg.visibilityField}
        visibilityAllowed={cfg.visibilityAllowed}
        invertVisibility={cfg.invertVisibility || false}
        dragHandleProps={dragHandleProps}
        isDragging={isDragging}
      />
    </div>
  );
};

export default ClientViewEditorSidebar;
